import { Outlet, useParams } from 'react-router'

import { Sidebar } from '@/app/layout/Sidebar'
import { Assistente } from '@/assistente/Assistente'
import { LimiteDoAssistente } from '@/assistente/LimiteDoAssistente'
import { useSession } from '@/auth/session'
import { FlagsProvider } from '@/flags/FlagsProvider'
import { PermissionProvider } from '@/permissions/PermissionProvider'
import { podeAcessar } from '@/tenant/clientes'

/**
 * Outermost layout: sidebar on the left, everything else to its right.
 *
 * Kept separate from `ModuleFrame` so the sidebar survives a module switch just as
 * the frame survives a tab switch. The floating AI assistant (`1j`) is mounted here
 * for the same reason: it is not a route, and its conversation has to survive a
 * module switch. It is rendered only inside a client the user can reach.
 *
 * **Both story 0.4 providers mount here**, and the position is load-bearing:
 *
 *  - INSIDE the session, because `PermissionProvider` reads `grupos` through
 *    `useSession()`. Wrapping `SessionProvider` instead would make it throw;
 *  - ABOVE the sidebar and every route, so permissions resolve once per session
 *    (AC-1) rather than once per screen that asks;
 *  - but BELOW `/login`, which has no session to resolve from and needs neither.
 *
 * `FlagsProvider` takes `idCliente` from the route because flags are per client
 * (AC-8), and the client's real `featureFlags` from the session's `/eu` profile.
 * `idCliente` is `undefined` on `/admin/*` and `/compliance/perguntas`, which are not
 * client-scoped — those modules are gated by permission alone.
 */
export function AppShell() {
  const { idCliente } = useParams()
  const { perfil, sessao } = useSession()
  // Os objetos vêm do perfil, então a identidade é estável entre renders e o
  // `useMemo` do provider não recalcula à toa. `undefined` no modo mock.
  const cliente = perfil?.clientes.find((candidato) => candidato.idCliente === idCliente)

  return (
    <PermissionProvider>
      <FlagsProvider
        idCliente={idCliente}
        featureFlags={cliente?.featureFlags}
        modulosHabilitados={cliente?.modulosHabilitados}
      >
        <div className="bg-page flex h-dvh">
          <Sidebar />
          <Outlet />
          {/* Só com um cliente que o usuário REALMENTE acessa: o shell fica acima do
              `TenantScope`, e a tela de "acesso negado" não deve trazer um chat junto.
              `key`: trocar de cliente remonta, e a conversa não atravessa tenants. */}
          {idCliente && podeAcessar(idCliente, sessao?.clientes ?? []) && (
            // O limite também leva a `key`: um erro num cliente não deixa o
            // assistente oculto para o próximo.
            <LimiteDoAssistente key={idCliente}>
              <Assistente idCliente={idCliente} />
            </LimiteDoAssistente>
          )}
        </div>
      </FlagsProvider>
    </PermissionProvider>
  )
}
